/**
 * Pro 2 diopter: the "Built-in Diopter" section from viture.com/pro2 (its "s11").
 * Scales the design stage like the source, reveals the copy once the section reaches 78% down the
 * viewport, then crossfades to the second look and brings in the diopter range and SGS badge at 60%.
 * The canvas plays a 30-frame dial turn on a loop while in view and can be dragged sideways to scrub.
 * Frame rate, hold time, drag ratio and load window are taken from the source page.
 */
if (!customElements.get('pro2-diopter')) {
  const MOBILE_QUERY = '(max-width: 640px)';
  /** @type {{ desktop: [number, number], mobile: [number, number] }} Design stage [width, height] */
  const STAGES = { desktop: [1920, 1080], mobile: [375, 812] };
  const FPS = 30;
  // Pause on the last frame before the loop restarts, and after a drag before it resumes
  const HOLD_MS = 2000;
  // Pixels of horizontal drag per frame
  const DRAG_PX = 9;
  // Movement before a touch is decided as a horizontal drag or a vertical scroll
  const TOUCH_SLOP = 8;
  const MAX_LOADS = 4;

  /** @typedef {HTMLImageElement & { loaded?: boolean }} FrameImage A frame, flagged once it can be drawn */

  /**
   * State set up by connectedCallback. Without a constructor, checkJs types these fields as possibly
   * undefined, so the methods declare `this` as ConnectedDiopter to read them as present.
   * @typedef {object} DiopterState
   * @property {MediaQueryList} mobileQuery
   * @property {boolean} reducedMotion
   * @property {string[]} urls
   * @property {(FrameImage | undefined)[]} frames
   * @property {number} frame Index of the frame to show
   * @property {number} drawn Index of the frame last drawn, -1 when the canvas needs a redraw
   * @property {boolean} loading
   * @property {boolean} inView
   * @property {boolean} dragging
   * @property {() => void} onResize
   * @property {() => void} onScroll
   * @property {number} resizeFrame
   * @property {IntersectionObserver[]} observers Not created when reduced motion is on
   * @property {number} generation Bumped on unload so loads still in flight are ignored
   * @property {number} playFrame
   * @property {number} holdTimer
   */

  /** @typedef {Pro2Diopter & DiopterState} ConnectedDiopter */

  class Pro2Diopter extends HTMLElement {
    /** @this {ConnectedDiopter} */
    connectedCallback() {
      /** @type {MediaQueryList} */
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      /** @type {boolean} */
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      /** @type {string[]} */
      this.urls = (this.dataset.frames || '').split(',').filter(Boolean);
      /** @type {(FrameImage | undefined)[]} */
      this.frames = [];
      /** @type {number} */
      this.frame = 0;
      /** @type {number} */
      this.drawn = -1;
      /** @type {boolean} */
      this.loading = false;
      /** @type {boolean} */
      this.inView = false;
      /** @type {boolean} */
      this.dragging = false;

      /** @type {() => void} */
      this.onResize = () => {
        cancelAnimationFrame(this.resizeFrame);
        /** @type {number} */
        this.resizeFrame = requestAnimationFrame(() => {
          this.#fit();
          this.#sizeCanvases();
          this.#update();
        });
      };
      /** @type {() => void} */
      this.onScroll = () => this.#update();
      this.#fit();
      this.#sizeCanvases();
      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.mobileQuery.addEventListener('change', this.onResize);

      if (!this.reducedMotion) {
        this.classList.add('is-armed');
        /** @type {IntersectionObserver[]} */
        this.observers = [
          this.#once('-22%', () => this.classList.add('is-revealed')),
          this.#once('-40%', () => this.classList.add('is-played')),
        ];
      }

      this.querySelectorAll('canvas').forEach((canvas) => this.#bindDrag(canvas));
      this.#update();
    }

    /** @this {ConnectedDiopter} */
    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      this.mobileQuery.removeEventListener('change', this.onResize);
      this.observers?.forEach((observer) => observer.disconnect());
      cancelAnimationFrame(this.resizeFrame);
      this.#stop();
      this.#unload();
    }

    /**
     * Run `callback` once the section top crosses `margin` from the bottom of the viewport
     * @param {string} margin
     * @param {() => void} callback
     * @returns {IntersectionObserver}
     */
    #once(margin, callback) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          callback();
          observer.disconnect();
        },
        { rootMargin: `0px 0px ${margin} 0px` }
      );
      observer.observe(this);
      return observer;
    }

    /**
     * Desktop covers the viewport; mobile is 375 wide and as tall as the viewport at that width.
     * Like the source, width includes the scrollbar
     * @this {ConnectedDiopter}
     */
    #fit() {
      const width = window.innerWidth || this.clientWidth;
      const height = this.clientHeight;
      if (this.mobileQuery.matches) {
        const scale = width / STAGES.mobile[0];
        this.style.setProperty('--stage-h', (height / scale).toFixed(2));
        this.style.setProperty('--scale', scale.toFixed(4));
      } else {
        const [stageWidth, stageHeight] = STAGES.desktop;
        this.style.removeProperty('--stage-h');
        this.style.setProperty('--scale', Math.max(width / stageWidth, height / stageHeight).toFixed(4));
      }
    }

    /**
     * Draw at the viewport's pixel width, between 768 and the frames' 1920
     * @this {ConnectedDiopter}
     */
    #sizeCanvases() {
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.min(1920, Math.max(768, Math.round(window.innerWidth * pixelRatio)));
      const height = Math.round((width / 1920) * 1080);
      this.querySelectorAll('canvas').forEach((canvas) => {
        if (canvas.width !== width) canvas.width = width;
        if (canvas.height !== height) canvas.height = height;
      });
      this.drawn = -1;
      this.#draw();
    }

    /**
     * The canvas in whichever stage is showing. Accessors can't take `@this`, hence the cast
     * @returns {HTMLCanvasElement | null}
     */
    get #canvas() {
      return this.querySelector((/** @type {MediaQueryList} */ (this.mobileQuery)).matches ? '.pro2-diopter__stage--m canvas' : '.pro2-diopter__stage--d canvas');
    }

    /** @this {ConnectedDiopter} */
    #draw() {
      const canvas = this.#canvas;
      const image = this.frames[this.frame];
      if (!canvas || !image?.loaded || (this.drawn === this.frame && this.drawnOn === canvas)) return;
      const context = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      this.drawn = this.frame;
      /** @type {HTMLCanvasElement} */
      this.drawnOn = canvas;
    }

    /**
     * @this {ConnectedDiopter}
     * @param {number} frame Clamped to the available frames
     */
    #show(frame) {
      this.frame = Math.max(0, Math.min(this.urls.length - 1, frame));
      this.#draw();
    }

    /**
     * Load frames within a screen of the viewport, four at a time; free them beyond two screens
     * @this {ConnectedDiopter}
     */
    #update() {
      const viewportHeight = window.innerHeight;
      const rect = this.getBoundingClientRect();
      if (rect.bottom > -viewportHeight && rect.top < 2 * viewportHeight) this.#load();
      else if (rect.bottom < -2 * viewportHeight || rect.top > 3 * viewportHeight) this.#unload();

      const inView = rect.bottom > 0 && rect.top < viewportHeight;
      if (inView === this.inView) return;
      this.inView = inView;
      if (this.reducedMotion) return;
      if (inView && !this.dragging) this.#play();
      else if (!inView) this.#stop();
    }

    /** @this {ConnectedDiopter} */
    #load() {
      if (this.loading) return;
      this.loading = true;
      const generation = (this.generation = (this.generation || 0) + 1);
      let nextIndex = 0;
      let activeLoads = 0;
      // Start loads until MAX_LOADS are in flight; each one that finishes starts the next
      const loadMore = () => {
        while (this.loading && generation === this.generation && activeLoads < MAX_LOADS && nextIndex < this.urls.length) {
          const index = nextIndex++;
          /** @type {FrameImage} */
          const image = new Image();
          image.decoding = 'async';
          this.frames[index] = image;
          activeLoads++;
          /** @param {boolean} succeeded */
          const finish = (succeeded) => {
            activeLoads--;
            if (generation !== this.generation) return;
            image.loaded = succeeded;
            if (succeeded && index === this.frame) this.#draw();
            loadMore();
          };
          image.addEventListener('load', () => image.decode().then(() => finish(true), () => finish(image.naturalWidth > 0)), { once: true });
          image.addEventListener('error', () => finish(false), { once: true });
          image.src = /** @type {string} */ (this.urls[index]);
        }
      };
      loadMore();
    }

    /** @this {ConnectedDiopter} */
    #unload() {
      if (!this.loading) return;
      this.loading = false;
      this.generation++;
      this.frames.forEach((image) => image && (image.src = ''));
      this.frames = [];
      this.drawn = -1;
    }

    /**
     * Play 0 → last at 30fps, hold, then start over
     * @this {ConnectedDiopter}
     */
    #play() {
      this.#stop();
      const startTime = performance.now();
      /** @param {number} now */
      const step = (now) => {
        const frame = Math.min(this.urls.length - 1, Math.floor(((now - startTime) / 1000) * FPS));
        this.#show(frame);
        if (frame >= this.urls.length - 1) {
          /** @type {number} */
          this.holdTimer = setTimeout(() => this.inView && !this.dragging && this.#play(), HOLD_MS);
          return;
        }
        /** @type {number} */
        this.playFrame = requestAnimationFrame(step);
      };
      this.playFrame = requestAnimationFrame(step);
    }

    /** @this {ConnectedDiopter} */
    #stop() {
      cancelAnimationFrame(this.playFrame);
      clearTimeout(this.holdTimer);
    }

    /**
     * Drag sideways to scrub the frames; the loop resumes after HOLD_MS
     * @this {ConnectedDiopter}
     * @param {HTMLCanvasElement} canvas
     */
    #bindDrag(canvas) {
      let startX = 0;
      let startY = 0;
      let startFrame = 0;
      // 'live' drags, 'pending' waits to see which way a touch goes, 'dead' ignores the gesture
      let dragState = 'dead';

      canvas.addEventListener('pointerdown', (event) => {
        this.dragging = true;
        this.#stop();
        startX = event.clientX;
        startY = event.clientY;
        startFrame = this.frame;
        dragState = event.pointerType === 'touch' ? 'pending' : 'live';
        try {
          canvas.setPointerCapture(event.pointerId);
        } catch {
          // Pointer already released
        }
        canvas.classList.add('is-dragging');
      });

      canvas.addEventListener('pointermove', (event) => {
        if (!this.dragging || dragState === 'dead') return;
        if (dragState === 'pending') {
          const distanceX = Math.abs(event.clientX - startX);
          const distanceY = Math.abs(event.clientY - startY);
          if (Math.max(distanceX, distanceY) < TOUCH_SLOP) return;
          if (distanceX <= distanceY) {
            dragState = 'dead';
            return;
          }
          dragState = 'live';
          startX = event.clientX;
          startFrame = this.frame;
        }
        this.#show(startFrame + Math.round((startX - event.clientX) / DRAG_PX));
      });

      const endDrag = () => {
        if (!this.dragging) return;
        this.dragging = false;
        dragState = 'dead';
        canvas.classList.remove('is-dragging');
        clearTimeout(this.holdTimer);
        this.holdTimer = setTimeout(() => this.inView && !this.dragging && !this.reducedMotion && this.#play(), HOLD_MS);
      };
      canvas.addEventListener('pointerup', endDrag);
      canvas.addEventListener('pointercancel', endDrag);
    }
  }

  customElements.define('pro2-diopter', Pro2Diopter);
}
